import styled from 'styled-components'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import {
  PRODUCT_CARD_SIZES,
  THUMBNAIL_STATES,
} from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { legacySlugToBestTabName } from '@/constants/best-seller-category-type.enum'
import { LocationEnum } from '@/constants/location.enum'
import { ProductSkeletonLengthEnum } from '@/constants/product-skeleton-length.enum'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { useBestEventTracker } from '@/containers/event-tracker/best-event-tracker'
import { useHomeMoreListTracker } from '@/containers/event-tracker/home-more-list.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import useBestSellerProductsListV3 from '@/containers/hooks/bestSeller/bestSellerProductsListV3.container.hook'
import { TrackingInfoType } from '@/types/event-tracker.type'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'
import { unset } from '@/utils/utilCommon'

type ListProductsProps = {
  trackingInfo?: TrackingInfoType
}

const ListProducts = ({ trackingInfo }: ListProductsProps) => {
  const { trackClickBestSellerProductEventV2 } = useBestEventTracker()
  const { trackClickHomeSectionProductEventV4 } = useHomeMoreListTracker()
  const { query } = useCustomRouter()
  const { categoryName } = query as { categoryName: string }

  const { isLoading, productListData, ref } = useBestSellerProductsListV3()

  const handleClickProduct = (product: ProductTypeDefinedByFrontend) => {
    trackClickHomeSectionProductEventV4({
      sectionName: legacySlugToBestTabName(categoryName),
      location: LocationEnum.List,
      product,
    })
    trackClickBestSellerProductEventV2({
      tabName: legacySlugToBestTabName(categoryName),
      location: LocationEnum.List,
      product,
    })
    if (!trackingInfo) {
      return
    }
    const _trackingInfo = trackingInfo
    unset(_trackingInfo, _trackingInfo.eventName)
  }
  return (
    <>
      {!isLoading ? (
        <StyledListLayout>
          {productListData?.pages
            ?.flatMap((page) => page.data?.products)
            .map((product, index) => {
              if (!product) {
                return null
              }

              const productCardData = transformProductTypeDefinedByFrontend(product)
              return (
                <div key={productCardData.id} ref={ref}>
                  <NewProductCard
                    product={productCardData}
                    thumbnailState={THUMBNAIL_STATES.rankingLabel}
                    size={PRODUCT_CARD_SIZES.half}
                    rankingNumber={index}
                    onClickProductCard={() => handleClickProduct(productCardData)}
                  />
                </div>
              )
            })}
        </StyledListLayout>
      ) : (
        <StyledSkeleton>
          <SkeletonProduct length={ProductSkeletonLengthEnum.Eight} size={ProductThumbnailSizeEnum.Fill} />
        </StyledSkeleton>
      )}
    </>
  )
}

export default ListProducts

const StyledListLayout = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: repeat(2, 1fr);
  grid-row-gap: 3.2rem;
  grid-column-gap: 0.8rem;
`

const StyledSkeleton = styled(StyledListLayout)`
  & > div {
    &:nth-child(1),
    &:nth-child(2) {
      margin-top: 0;
    }
  }
`
